import React, { useCallback, useMemo } from 'react';
import { CustomDynamicResultTable, ExpandableRow, TableHead, TableBody, ColGroup } from '@css-xp/base';
import { errorStateProp, emptyStateProp } from '@css-xp/ppc-common';
import { LinkedEntitlements, PresentationLayerProducts, Product } from '../../../../../product-line-items/src/types';
import { FailureProductLineItemRow } from './failure-product-line-item-row';
import Badge from '@atlaskit/badge';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import './failure-product-rows-container.scss';

const getColumns = () => {
  const columns = [
    {
      name: 'Product',
      customClassNames: 'col-common',
    },
    {
      name: 'Site/Org',
      customClassNames: 'col-site',
    },
    {
      name: 'Change',
      customClassNames: 'col-common',
    },
  ];
  return columns;
};

type Props = {
  products: PresentationLayerProducts;
};

export const FailureProductRowsContainer = ({ products }: Props) => {
  const getProductRow = (product: Product) => {
    return <FailureProductLineItemRow lineItem={product} isApp={false} />;
  };

  const columns = getColumns();

  const getAppRows = (linkedEntitlements: LinkedEntitlements) => {
    const [_, ...remainingColumns] = columns;
    const remainingEmptyCells = remainingColumns.map((_, index) => <td key={index} />);

    const subRows = linkedEntitlements.map((app, index) => (
      <FailureProductLineItemRow key={`${app.id}_${index}`} lineItem={app} isApp={true} />
    ));

    const expandableCell = (
      <td key="expandableCell">
        Integrated apps <Badge appearance="primary">{linkedEntitlements.length}</Badge>
      </td>
    );

    const expandableRowHeading = [[expandableCell, ...remainingEmptyCells]];

    return <ExpandableRow rowsAsCells={expandableRowHeading} subRows={subRows} />;
  };

  const rows = useMemo(() => {
    // `productRows` will contain list of products as a table row
    let productRows: React.ReactNode[] = [];

    /**
     * Building table rows based on the list of products a user selected.
     * A product can be standalone or part of a container. Same is decided based on the value of `isContainer` property.
     * If product is standalone, we can directly render the row and then render the apps (apps render pending implementation).
     * If product is part of a container, we need to render it along with all of it's siblings together and then render the apps (apps render pending implementation).
     */
    products.forEach((product) => {
      const isJiraContainer = product.type === 'container';

      if (!isJiraContainer) {
        const productRow = getProductRow(product);
        productRows.push(productRow);
      } else {
        const jiraProductRows = product.products.map((jiraProduct) => {
          const productRow = getProductRow(jiraProduct);
          return productRow;
        });

        const updatedProductRows = productRows.concat(jiraProductRows);
        productRows = updatedProductRows;
      }

      if (product?.linkedEntitlements?.length > 0) {
        const appRows = getAppRows(product.linkedEntitlements);

        productRows.push(appRows);
      }
    });

    return productRows;
  }, [getAppRows, getProductRow, products]);

  return (
    <div className="failure-product-rows-container">
      <CustomDynamicResultTable
        isLoading={false}
        noResults={false}
        isPaginationEnabled={false}
        selectableResults={true}
        error={null}
        boxShadow={false}
        emptyStateProps={emptyStateProp}
        errorStateProps={errorStateProp}
        hasTableWrapper={false}
        shouldShowHorizontalScrollAnimation={false}
      >
        <ColGroup columns={columns} />
        <TableHead columns={columns} />
        <TableBody>{rows}</TableBody>
      </CustomDynamicResultTable>
    </div>
  );
};
